import { formatTimeAgo } from "@/shared/lib/date-time";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  Button,
  Checkbox,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import type { RowSelectionState } from "@tanstack/react-table";
import {
  EyeIcon,
  EyeOffIcon,
  PackageIcon,
  RotateCcwIcon,
  StoreIcon,
  TrashIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { ListingProductListItem } from "../api/client";

function StatusSummary({ row }: { row: ListingProductListItem }) {
  const parts: Array<{ label: string; variant: "success" | "secondary" | "destructive" }> = [];
  if (row.activeCount > 0) {
    parts.push({ label: `${row.activeCount} ໃຊ້ງານ`, variant: "success" });
  }
  if (row.hiddenCount > 0) {
    parts.push({ label: `${row.hiddenCount} ເຊື່ອງ`, variant: "secondary" });
  }
  if (row.removedCount > 0) {
    parts.push({ label: `${row.removedCount} ລຶບ`, variant: "destructive" });
  }
  if (parts.length === 0) {
    return <Badge variant="secondary">—</Badge>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {parts.map((p) => (
        <Badge key={p.label} variant={p.variant} className="text-[10px]">
          {p.label}
        </Badge>
      ))}
    </div>
  );
}

function ProductCell({ row }: { row: { original: ListingProductListItem } }) {
  const r = row.original;
  const [imgError, setImgError] = useState(false);
  const src = r.imageUrl ? resolveImageSrc(r.imageUrl) : null;
  const showImage = Boolean(src && !imgError);

  return (
    <div className="flex items-center gap-3">
      <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
        {showImage ? (
          <img
            src={src ?? undefined}
            alt=""
            className="size-10 object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <PackageIcon className="size-5 text-muted-foreground" aria-hidden />
        )}
      </div>
      <div className="min-w-0">
        <div className="truncate font-medium">{r.productName}</div>
        <div className="text-muted-foreground text-xs">
          {r.skuCount} SKU · {r.shopCount} ຮ້ານ · {r.listingCount} listing
        </div>
      </div>
    </div>
  );
}

type ListingProductsTableProps = {
  isLoading: boolean;
  data: ListingProductListItem[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onView: (row: ListingProductListItem) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  selectedCount?: number;
  onClearSelection?: () => void;
  onBulkHide?: (ids: string[]) => void;
  onBulkRemove?: (ids: string[]) => void;
  onBulkRestore?: (ids: string[]) => void;
  tableKey?: number;
};

export function ListingProductsTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onView,
  onPaginationChange,
  onSortingChange,
  rowSelection = {},
  onRowSelectionChange,
  selectedCount = 0,
  onClearSelection,
  onBulkHide,
  onBulkRemove,
  onBulkRestore,
  tableKey,
}: ListingProductsTableProps) {
  const columns: TanstackReactTable.ColumnDef<ListingProductListItem>[] = useMemo(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected()
                ? true
                : table.getIsSomePageRowsSelected()
                  ? "indeterminate"
                  : false
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(value === true)
            }
            aria-label="ເລືອກທັງໝົດ"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(value === true)}
            aria-label="ເລືອກແຖວ"
          />
        ),
        size: 50,
      },
      {
        id: "product",
        header: "ສິນຄ້າ",
        cell: ({ row }) => <ProductCell row={row} />,
        size: 280,
      },
      createSortableColumn<ListingProductListItem>("listingCount", "Listing", {
        size: 90,
        cell: ({ row }) => (
          <span className="font-medium tabular-nums">
            {row.original.listingCount}
          </span>
        ),
      }),
      {
        id: "shops",
        header: "ຮ້ານ",
        cell: ({ row }) => (
          <span className="flex items-center gap-1.5 text-sm">
            <StoreIcon className="size-3.5 text-muted-foreground" aria-hidden />
            {row.original.shopCount}
          </span>
        ),
        size: 80,
      },
      {
        id: "status",
        header: "ສະຖານະ",
        cell: ({ row }) => <StatusSummary row={row.original} />,
        size: 180,
      },
      createSortableColumn<ListingProductListItem>(
        "lastUpdatedAt",
        "ອັບເດດ",
        {
          size: 120,
          cell: ({ row }) => (
            <span className="text-muted-foreground text-xs">
              {formatTimeAgo(row.original.lastUpdatedAt)}
            </span>
          ),
        },
      ),
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <RowActions
            actions={[
              {
                label: "ເບິ່ງລາຍລະອຽດ",
                icon: <EyeIcon className="size-4" aria-hidden />,
                onClick: () => onView(row.original),
              },
            ]}
            maxInline={1}
          />
        ),
        size: 120,
      },
    ],
    [onView],
  );

  const selectedListingIds = useMemo(() => {
    if (selectedCount === 0) return [];
    return data
      .filter((_, idx) => rowSelection[String(idx)] === true)
      .flatMap((row) => row.listingIds);
  }, [data, rowSelection, selectedCount]);

  return (
    <div className="flex flex-col">
      {selectedCount > 0 && (
        <div className="flex flex-wrap items-center gap-2 border bg-muted/50 px-3 py-2 text-sm">
          <span className="text-muted-foreground">
            ເລືອກ {selectedCount} ສິນຄ້າ ({selectedListingIds.length} listing)
          </span>

          <div className="flex w-full flex-1 justify-between gap-2">
            <div className="flex gap-2">
              {onBulkHide && selectedListingIds.length > 0 && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => onBulkHide(selectedListingIds)}
                >
                  <EyeOffIcon className="size-4" aria-hidden />
                  ເຊື່ອງ
                </Button>
              )}
              {onBulkRemove && selectedListingIds.length > 0 && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onBulkRemove(selectedListingIds)}
                >
                  <TrashIcon className="size-4" aria-hidden />
                  ລຶບ
                </Button>
              )}
              {onBulkRestore && selectedListingIds.length > 0 && (
                <Button
                  size="sm"
                  onClick={() => onBulkRestore(selectedListingIds)}
                >
                  <RotateCcwIcon className="size-4" aria-hidden />
                  ເປີດຄືນ
                </Button>
              )}
            </div>

            {onClearSelection && (
              <button
                type="button"
                onClick={onClearSelection}
                className="text-secondary hover:underline"
              >
                ລ້າງການເລືອກ
              </button>
            )}
          </div>
        </div>
      )}

      <DataTable<ListingProductListItem, unknown>
        key={tableKey}
        noDataMessage="ບໍ່ພົບລາຍການສິນຄ້າ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (!sorting[0]?.id) return;
          onSortingChange(sorting[0].id as string, !!sorting[0].desc);
        }}
        enableRowSelection
        onRowSelectionChange={onRowSelectionChange}
      />
    </div>
  );
}
